import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

/**
 * L'utilisateur connecté peut-il ouvrir le tableau de bord de CETTE organisation ?
 *
 * Les routes `/association/:orgId` et `/club/:orgId` ne vérifiaient que le RÔLE
 * (`association_staff`, `club_staff`) : n'importe quel responsable ouvrait le
 * tableau de bord d'une autre organisation en changeant l'id (RET-23). La base
 * ne laissait rien fuiter (RLS), mais la page s'affichait vide ou en erreur.
 *
 * Accès : une ligne `club_staff` (le bureau y figure en `coordinator`), le
 * créateur de l'organisation (2 organisations de DEMO n'ont pas de ligne
 * `club_staff` pour leur créateur), ou un super_admin.
 */
export function useOrganizationAccess(organizationId?: string) {
  const { user, isSuperAdmin } = useAuth();
  const superAdmin = isSuperAdmin();

  const { data: hasAccess, isLoading } = useQuery({
    queryKey: ['organization-access', organizationId, user?.id],
    enabled: !!user?.id && !!organizationId && !superAdmin,
    queryFn: async () => {
      const [staff, org] = await Promise.all([
        supabase
          .from('club_staff')
          .select('id')
          .eq('user_id', user!.id)
          .eq('organization_id', organizationId!)
          .limit(1),
        supabase.from('organizations').select('created_by').eq('id', organizationId!).maybeSingle(),
      ]);
      if (staff.error) throw staff.error;
      if (org.error) throw org.error;
      return (staff.data?.length ?? 0) > 0 || org.data?.created_by === user!.id;
    },
  });

  if (superAdmin) return { hasAccess: true, isLoading: false };
  return { hasAccess: hasAccess === true, isLoading: !!user && !!organizationId && isLoading };
}
